import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import MiniTiptapEditor from "../../../component/MiniTiptapEditor";
import { useAuth } from "../../../global/AuthContext";
import CommentItem from "../boardClasses/CommentItem";
import { freeBoardApi, FreeComment } from "./api";
import { isAxiosError } from "axios";
import "../../../assets/css/container/board/ViewPost.css";

function message(error: unknown) {
    if (isAxiosError(error)) {
        if (error.response?.status === 401) return "로그인이 필요합니다.";
        if (error.response?.status === 403) return "댓글 작성자만 수정하거나 삭제할 수 있습니다.";
        if (error.response?.status === 404) return "댓글 또는 게시글을 찾을 수 없습니다.";
        if (typeof error.response?.data?.message === "string") return error.response.data.message;
    }
    return "댓글 요청을 처리하지 못했습니다. 잠시 후 다시 시도해주세요.";
}
export default function FreePostComments({ postId }: { postId: string }) {
    const { user } = useAuth();
    const [comments, setComments] = useState<FreeComment[]>([]);
    const [content, setContent] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [retry, setRetry] = useState(0);
    const [busy, setBusy] = useState(false);
    const pending = useRef(false);
    useEffect(() => {
        const controller = new AbortController();
        setLoading(true); setLoadFailed(false); setError("");
        freeBoardApi.comments(postId, controller.signal)
            .then(res => { if (!controller.signal.aborted) setComments(res.data); })
            .catch(err => { if (!controller.signal.aborted) { setError(message(err)); setLoadFailed(true); } })
            .finally(() => { if (!controller.signal.aborted) setLoading(false); });
        return () => controller.abort();
    }, [postId, retry]);
    const mutate = async (action: () => Promise<void>) => {
        if (pending.current) return false;
        pending.current = true; setBusy(true); setError("");
        try { await action(); return true; }
        catch (err) { setError(message(err)); return false; }
        finally { pending.current = false; setBusy(false); }
    };
    const add = () => mutate(async () => {
        const draft = content;
        const res = await freeBoardApi.addComment(postId, draft);
        setComments(prev => [...prev, res.data]);
        setContent(current => current === draft ? "" : current);
    });
    const update = (id: number, next: string) => mutate(async () => {
        const res = await freeBoardApi.updateComment(id, next);
        setComments(prev => prev.map(comment => comment.id === id ? res.data : comment));
    });
    const remove = async (id: number) => {
        if (!window.confirm("댓글을 삭제하시겠습니까?")) return;
        await mutate(async () => {
            await freeBoardApi.deleteComment(id);
            setComments(prev => prev.filter(comment => comment.id !== id));
        });
    };
    return <section className="BoardDetail" aria-label="자유게시판 댓글">
        <h2>댓글 {comments.length}</h2>
        {error && <p role="alert">{error}</p>}
        {loading ? <p role="status">댓글을 불러오는 중...</p> : loadFailed ?
            <button onClick={() => setRetry(value => value + 1)}>댓글 다시 불러오기</button> : <>
            {user ? <div className="CommentWrite">
                <MiniTiptapEditor content={content} onChange={setContent}/>
                <div className="CommentWriteActions"><button className="CommentSubmit" disabled={busy} onClick={add}>댓글 작성</button></div>
            </div> : <p><Link to="/login">로그인</Link> 후 댓글을 작성할 수 있습니다.</p>}
            <div className="CommentList" aria-busy={busy}>
                {comments.length === 0 ? <p className="NoComments">아직 댓글이 없습니다.</p> : comments.map(comment =>
                    <CommentItem key={comment.id} comment={comment} currentUserId={user?.id} onUpdate={update} onDelete={remove}/>)}
            </div>
        </>}
    </section>;
}
